/**
 * TicketDetailPage — view and update a single support ticket including comments.
 *
 * Route: /help/tickets/:id
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import {
  supportApi,
  type TicketStatus,
  type TicketPriority,
  type Ticket,
} from '../api/support';
import { CommentList } from '../components/CommentList';

const VALID_TRANSITIONS: Record<TicketStatus, TicketStatus[]> = {
  open: ['in_progress'],
  in_progress: ['waiting_on_customer'],
  waiting_on_customer: ['resolved'],
  resolved: ['closed'],
  closed: [],
};

const STATUS_COLOR: Record<TicketStatus, string> = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-indigo-100 text-indigo-800',
  waiting_on_customer: 'bg-amber-100 text-amber-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-700',
};

const PRIORITY_COLOR: Record<TicketPriority, string> = {
  low: 'text-green-600',
  medium: 'text-yellow-600',
  high: 'text-orange-600',
  critical: 'text-red-600',
};

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900">{value ?? <span className="text-gray-400 italic">—</span>}</dd>
    </div>
  );
}

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [patchError, setPatchError] = useState<string | null>(null);

  const { data: ticket, isLoading, isError } = useQuery<Ticket>({
    queryKey: ['ticket', id],
    queryFn: () => supportApi.getTicket(id!),
    enabled: Boolean(id),
  });

  const patchMutation = useMutation({
    mutationFn: (patch: Parameters<typeof supportApi.updateTicket>[1]) =>
      supportApi.updateTicket(id!, { ...patch, version: ticket?.version }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['ticket', id], updated);
      setPatchError(null);
    },
    onError: (err) => {
      setPatchError((err as Error).message ?? 'Update failed');
    },
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-center text-sm text-gray-400">
        Loading ticket…
      </div>
    );
  }

  if (isError || !ticket) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-center">
        <AlertTriangle className="mx-auto mb-2 w-8 h-8 text-red-400" />
        <p className="text-sm text-red-600">Ticket not found or access denied.</p>
        <Link to="/help/tickets" className="mt-4 inline-block text-sm text-indigo-600 hover:underline">
          Back to tickets
        </Link>
      </div>
    );
  }

  const nextStatuses = VALID_TRANSITIONS[ticket.status];

  function handleStatusTransition(newStatus: TicketStatus) {
    patchMutation.mutate({ status: newStatus });
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      {/* Back link */}
      <Link
        to="/help/tickets"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Tickets
      </Link>

      {patchError && (
        <div className="mb-4 rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
          {patchError}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <h1 className="text-xl font-bold text-gray-900 leading-tight">{ticket.subject}</h1>
        <span
          className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_COLOR[ticket.status]}`}
        >
          {ticket.status.replace(/_/g, ' ')}
        </span>
      </div>

      {/* Metadata grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 rounded-lg border border-gray-200 bg-white p-4 mb-6">
        <Field label="Priority" value={
          <span className={`font-medium ${PRIORITY_COLOR[ticket.priority]}`}>
            {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
          </span>
        } />
        <Field label="Category" value={ticket.category} />
        <Field label="Reporter" value={ticket.reporter_id} />
        <Field label="Assignee" value={ticket.assignee_id ?? null} />
        <Field label="Created" value={format(new Date(ticket.created_at), 'MMM d, yyyy HH:mm')} />
        <Field
          label="SLA Due"
          value={
            ticket.sla_due_at ? (
              <span className={new Date(ticket.sla_due_at) < new Date() ? 'text-red-600 font-medium' : ''}>
                {format(new Date(ticket.sla_due_at), 'MMM d, yyyy HH:mm')}
              </span>
            ) : null
          }
        />
        {ticket.resolved_at && (
          <Field label="Resolved" value={format(new Date(ticket.resolved_at), 'MMM d, yyyy HH:mm')} />
        )}
        <Field label="Version" value={String(ticket.version)} />
      </div>

      {/* Description */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Description</h2>
        <p className="text-sm text-gray-800 whitespace-pre-wrap">{ticket.description}</p>
      </div>

      {/* Attachments */}
      {ticket.attachments.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Attachments</h2>
          <ul className="space-y-1.5">
            {ticket.attachments.map((a) => (
              <li key={a.id} className="flex items-center gap-2 text-sm">
                {a.url ? (
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-indigo-600 hover:underline truncate max-w-xs"
                  >
                    {a.filename}
                    <ExternalLink className="w-3 h-3 shrink-0" />
                  </a>
                ) : (
                  <span className="text-gray-700">{a.filename}</span>
                )}
                <span className="text-gray-400 text-xs">({(a.size / 1024).toFixed(1)} KB)</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Status transitions */}
      {nextStatuses.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Transition Status</h2>
          <div className="flex items-center gap-2 flex-wrap">
            {nextStatuses.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => handleStatusTransition(s)}
                disabled={patchMutation.isPending}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Mark as {s.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Comments */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <CommentList ticketId={ticket.id} />
      </div>
    </div>
  );
}

export default TicketDetailPage;
